import type { PanelField } from "../panel/spec";
import { useState } from "react";
import { Accordion } from "../components/rumonai";

/** A checked list's answers by their names, as the form offers them. */
type Named = { value: string; label: string }[];

/**
 * A field's declared default, in the words its own box would show.
 *
 * The same words as the form's (`said` in `panel/form.tsx`): a choice by its
 * declared name, a switch as 是 / 否, nothing as 没填. A short list is named
 * item by item, a longer one or a table is counted, and a group of settings is
 * called one -- never JSON on a page.
 */
export function defaultWords(field: PanelField, named?: Named): string {
  const value = field.default;
  const word = (item: unknown) => {
    const text = String(item);
    return field.choice_labels?.[text] ?? named?.find((option) => option.value === text)?.label ?? text;
  };
  if (value === undefined || value === null || value === "") return "没填";
  if (typeof value === "boolean") return value ? "是" : "否";
  if (Array.isArray(value)) {
    if (!value.length) return "没填";
    if (value.length > 3 || value.some((item) => item !== null && typeof item === "object")) return `${value.length} 项`;
    return value.map(word).join("、");
  }
  if (typeof value === "object") return Object.keys(value).length ? "一组设置" : "没填";
  return word(value);
}

/**
 * The settings somebody stored on this page, each offered back to its
 * declared default (ruling 2026-09-30).
 *
 * Only a field the runtime reported as stored is listed: one nobody stored
 * already reads as its default. Emptying a box is not this -- that saves an
 * empty value, or nothing -- so going back is its own button, beside the name
 * of what it goes back to.
 */
export function ResetDefaults({
  fields,
  named,
  busy,
  onReset,
}: {
  fields: PanelField[];
  /** Answers of the checked lists on this page, by field key. */
  named?: Record<string, Named>;
  busy: boolean;
  onReset: (key: string) => void;
}) {
  const [open, setOpen] = useState<string[]>([]);
  if (!fields.length) return null;
  return (
    <Accordion className="connection-more connection-defaults" forceMount value={open} onValueChange={setOpen}
      items={[{ value: "defaults", title: `改回默认（${fields.length}）`, content: <>{fields.map((field) => (
        <div key={field.key} className="connection-default">
          <span>{field.label}</span>
          <span className="micro">默认：{defaultWords(field, named?.[field.key])}</span>
          <button
            type="button"
            className="btn btn-quiet"
            data-reset={field.key}
            disabled={busy}
            onClick={() => onReset(field.key)}
          >
            改回默认
          </button>
        </div>
      ))}</> }]} />
  );
}
